import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useSiteContent } from "@/context/SiteContentContext";
import { useT } from "@/context/LanguageContext";

/**
 * AnnouncementBar — the SKLUM / THE MASIE-style scrolling marquee that
 * sits above the header. Renders one OR two admin-editable messages
 * (per language), scrolling as a single continuous marquee. The
 * `{threshold}` placeholder is substituted with the current €
 * free-shipping threshold on the fly.
 *
 * Positioning trick — the bar and the header BOTH read `--gl-topbar-shift`
 * from the document root. When the visitor scrolls past the bar height
 * the shift becomes `-BAR_HEIGHT` so the bar slides off-screen AND the
 * header slides up to occupy that space. Reverse on scroll-up. Because
 * both surfaces share the same shift value they stay glued together no
 * matter which direction the visitor scrolls (including iOS rubber-band).
 */

const DEFAULT_MESSAGES = {
  en: ["Free shipping on orders over €{threshold}", "Ships from Lisbon in 1–2 business days"],
  pt: ["Envio grátis em encomendas acima de €{threshold}", "Enviado de Lisboa em 1–2 dias úteis"],
  es: ["Envío gratis en pedidos superiores a €{threshold}", "Enviado desde Lisboa en 1–2 días hábiles"],
};

const BASE_HEIGHT = 17;   // reduced ~40% from 28px per user request
const SALE_HEIGHT = 28;

const formatThreshold = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "";
  return Number.isInteger(v) ? `${v}` : v.toFixed(2);
};

const AnnouncementBar = () => {
  const { settings } = useSiteContent();
  const { lang } = useT();
  const sale = settings?.sale_banner || null;
  const enabled = Boolean(sale) || Boolean(settings?.announcement_bar_enabled);
  const BAR_HEIGHT = sale ? SALE_HEIGHT : BASE_HEIGHT;

  // Publish the bar height as a CSS custom prop so the fixed Header
  // slides down by exactly that amount when the bar is showing. And
  // wire a scroll listener that flips `--gl-topbar-shift` — the value
  // both the bar and the header add to their `top` — so they translate
  // together (bar off-screen, header snaps to viewport top) once the
  // visitor scrolls past the bar height.
  useEffect(() => {
    const root = document.documentElement;
    if (!enabled) {
      root.style.setProperty("--gl-announce-h", "0px");
      root.style.setProperty("--gl-topbar-shift", "0px");
      return undefined;
    }
    root.style.setProperty("--gl-announce-h", `${BAR_HEIGHT}px`);
    const threshold = BAR_HEIGHT;
    const onScroll = () => {
      const y = window.scrollY || document.documentElement.scrollTop || 0;
      root.style.setProperty(
        "--gl-topbar-shift",
        y > threshold ? `-${BAR_HEIGHT}px` : "0px"
      );
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      root.style.setProperty("--gl-announce-h", "0px");
      root.style.setProperty("--gl-topbar-shift", "0px");
    };
  }, [enabled, BAR_HEIGHT]);

  if (!enabled) return null;

  // Pull message 1 + optional message 2 from the admin settings. Both
  // fall back to the compiled defaults for the active language, then
  // to English if that language wasn't configured.
  const text = settings?.announcement_bar_text || {};
  // MSG 2 can be independently disabled from /admin so the admin can keep
  // the copy in place while temporarily showing only Message 1. Absent
  // flag = ON (backward-compat with pre-toggle installations).
  const msg2Enabled = settings?.announcement_bar_msg2_enabled !== false;
  const pickPair = (obj) => [obj?.[lang]?.[0], obj?.[lang]?.[1]];
  let m1 = text?.[lang]?.["1"] ?? text?.[lang]?.msg1 ?? text?.[lang];
  let m2 = text?.[lang]?.["2"] ?? text?.[lang]?.msg2;
  // Old flat-string schema — treat the whole string as message 1.
  if (typeof m1 !== "string") m1 = null;
  if (typeof m2 !== "string") m2 = null;
  m1 = m1 || text?.en?.["1"] || text?.en?.msg1 || (typeof text?.en === "string" ? text.en : null) || DEFAULT_MESSAGES[lang]?.[0] || DEFAULT_MESSAGES.en[0];
  m2 = msg2Enabled
    ? (m2 || text?.en?.["2"] || text?.en?.msg2 || DEFAULT_MESSAGES[lang]?.[1] || DEFAULT_MESSAGES.en[1])
    : null;
  void pickPair;

  const thresholdStr = formatThreshold(settings.free_shipping_threshold ?? 150);
  const substitute = (s) => (s || "").replaceAll("{threshold}", thresholdStr).trim();

  const saleMsg = sale ? (sale.text?.[lang] || sale.text?.pt || sale.text?.en || "") : "";
  const messages = sale ? [substitute(saleMsg)].filter(Boolean) : [substitute(m1), substitute(m2)].filter(Boolean);
  if (messages.length === 0) return null;

  // Repeat enough times for a seamless marquee at any viewport width.
  const REPEATS = 6;
  const track = new Array(REPEATS).fill(0);

  const Wrap = sale ? Link : "div";
  return (
    <Wrap
      {...(sale ? { to: sale.link || "/collection/all" } : {})}
      data-testid={sale ? "sale-banner" : "announcement-bar"}
      className={`gl-announce-bar fixed left-0 right-0 overflow-hidden select-none block ${sale ? "" : "bg-[#241C18] text-[#F1ECE2]"}`}
      style={{ height: `${BAR_HEIGHT}px`, ...(sale ? { background: sale.bg, color: sale.color } : {}) }}
      aria-live="polite"
    >
      <div className="gl-announce-track flex items-center whitespace-nowrap h-full">
        {track.map((_, i) => (
          <span
            key={i}
            className={`flex items-center font-badge tracking-[0.22em] uppercase ${sale ? "text-[11px] md:text-[12.5px] font-semibold" : "text-[9.5px] md:text-[10.5px]"}`}
          >
            {messages.map((msg, j) => (
              <span key={j} className="flex items-center">
                <span className="mx-14 md:mx-20">{msg}</span>
                <span className="opacity-40">✦</span>
              </span>
            ))}
          </span>
        ))}
      </div>
    </Wrap>
  );
};

export default AnnouncementBar;
